// externals
import type { ReactNode } from 'react';

// components
import { Grid } from '@interstate/components/Grid';
import {
    CompactVehicleImage,
    VehicleTitle,
    VinStockRow,
    MilesOdometer,
    SellingPrice,
    AgeOfInventory,
    DrivetrainDescription,
    ExteriorInteriorColor
} from './components';

// types
import { EditVehicle } from './EditVehicle';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { useMediaQuery } from 'react-responsive';
import type { useVehicleListRowItemInStock } from '../useVehicleListRowItemInStock';
import { getCompactGridBreakpoints } from './getCompactGridBreakpoints';

type VehicleListRowItemInStockCompactSelectedWithInputsProps = {
    vehicleData: ReturnType<typeof useVehicleListRowItemInStock>;
    forceSmallBreakpoint?: boolean;
    /** Rendered below the inputs, inside the same grid column, so it keeps their left and right edges at every width. */
    inlineActions?: ReactNode;
};

export const VehicleListRowItemInStockCompactSelectedWithInputs = ({
    vehicleData,
    forceSmallBreakpoint,
    inlineActions
}: VehicleListRowItemInStockCompactSelectedWithInputsProps) => {
    const {
        imageUrl,
        year,
        make,
        model,
        vin,
        stockNumber,
        vehicleListItemTitle,
        customMileage,
        isNewVehicle,
        customListedPrice,
        customRetailPrice,
        minOdometerValue,
        handleCustomMileageChange,
        handleCustomSellingPrice,
        handleCustomRetailPrice,
        formattedPrice,
        driveLine,
        exteriorColor,
        interiorColor,
        ageDays,
        shouldShowAgeOfInventory,
        shouldAutoFocusInputs
    } = vehicleData;

    const theme = useInterstateTheme();
    const isBreakpointDownXl = useMediaQuery({ maxWidth: theme.breakpoints.values.xl });

    const getVehicleDetailStyles = () => {
        // Media query not testable in JSDOM
        /* istanbul ignore next */
        if (forceSmallBreakpoint || isBreakpointDownXl) {
            return { display: 'flex', flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 2 };
        }
        return { display: 'flex', flexDirection: 'column' as const, gap: 1 };
    };

    return (
        <Grid container spacing={1} alignItems="center" flexGrow={1}>
            <Grid
                {...getCompactGridBreakpoints({ xs: 12, lg: 'auto', forceSmallBreakpoint })}
                display="flex"
                alignItems="center"
                gap={1}
            >
                <CompactVehicleImage vehicle={{ imageUrl, year, make, model }} />
            </Grid>
            <Grid
                {...getCompactGridBreakpoints({ xs: 12, xlOffset: 0.2, xl: 10, forceSmallBreakpoint })}
                display="flex"
                flexDirection="column"
                wrap="wrap"
                flexGrow={1}
            >
                <Grid container spacing={1} alignItems="center" flexGrow={1}>
                    <Grid
                        {...getCompactGridBreakpoints({ xs: 12, xl: 6, forceSmallBreakpoint })}
                        display="flex"
                        flexDirection="column"
                        gap={1}
                    >
                        <VehicleTitle title={vehicleListItemTitle} />
                        <VinStockRow vin={vin} stockNumber={stockNumber} />
                    </Grid>
                    <Grid {...getCompactGridBreakpoints({ xs: 12, xl: 3, forceSmallBreakpoint })} {...getVehicleDetailStyles()}>
                        <MilesOdometer mileage={customMileage} />
                        <ExteriorInteriorColor exteriorColor={exteriorColor} interiorColor={interiorColor} />
                    </Grid>
                    <Grid {...getCompactGridBreakpoints({ xs: 12, xl: 3, forceSmallBreakpoint })} {...getVehicleDetailStyles()}>
                        <DrivetrainDescription driveLine={driveLine} />
                        <SellingPrice formattedPrice={formattedPrice} />
                        {shouldShowAgeOfInventory && <AgeOfInventory ageDays={ageDays} />}
                    </Grid>
                </Grid>
                <EditVehicle
                    isMsrpRequired={isNewVehicle}
                    odometer={customMileage}
                    sellingPrice={customListedPrice}
                    msrp={customRetailPrice}
                    onMsrpChange={handleCustomRetailPrice}
                    onOdometerChange={handleCustomMileageChange}
                    onSellingPriceChange={handleCustomSellingPrice}
                    minOdometerValue={minOdometerValue}
                    vin={vin}
                    forceSmallBreakpoint={forceSmallBreakpoint}
                    autoFocus={shouldAutoFocusInputs}
                />
                {inlineActions && (
                    // Side by side beneath the inputs, on the grid EditVehicle lays those out on, so each action is an
                    // input wide: the slot spans the last two input columns and each child takes half of it, leaving a
                    // single action under the last input. A row with no inputs stacks its actions instead — see the
                    // sibling without-inputs component.
                    <Grid container columnSpacing={2} marginTop={2}>
                        <Grid
                            {...getCompactGridBreakpoints({ xs: 12, md: 8, mdOffset: 4, forceSmallBreakpoint })}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                                gap: 1,
                                '& > *': { flex: '0 1 calc(50% - 4px)' }
                            }}
                        >
                            {inlineActions}
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};
